import React from 'react';
import { FONTS } from '../../styles/tokens';
import type { AdminNotification } from '../../lib/notifications-api';

interface Colors { text: string; textSecondary: string; card: string; border: string; bg: string }

interface NotificationDrawerProps {
  open: boolean;
  items: AdminNotification[];
  colors: Colors;
  onClose: () => void;
  onMarkAllRead: () => void;
  onMarkOneRead: (id: string) => void;
}

const SEVERITY_COLORS: Readonly<Record<AdminNotification['severity'], string>> = {
  info: '#01A9ED',
  warn: '#F59E0B',
  error: '#EF4444',
  success: '#10B981',
};

/** Right-side slide-over panel listing recent notifications. */
export function NotificationDrawer(props: NotificationDrawerProps): React.JSX.Element | null {
  if (!props.open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
      <div
        onClick={props.onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
      />
      <aside
        style={{
          position: 'absolute', top: 0, right: 0, height: '100%',
          width: '380px', maxWidth: '94vw',
          background: props.colors.bg, borderLeft: `1px solid ${props.colors.border}`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        <Header colors={props.colors} onClose={props.onClose} onMarkAllRead={props.onMarkAllRead} />
        <div style={{ overflowY: 'auto', padding: '8px 12px 20px' }}>
          {props.items.length === 0 && (
            <p style={{ fontFamily: FONTS.body, fontSize: '13px', color: props.colors.textSecondary, padding: '20px 8px' }}>
              No notifications yet.
            </p>
          )}
          {props.items.map(item => (
            <Item key={item.id} item={item} colors={props.colors} onClick={() => props.onMarkOneRead(item.id)} />
          ))}
        </div>
      </aside>
    </div>
  );
}

/** Drawer header with title + actions. */
function Header(props: { colors: Colors; onClose: () => void; onMarkAllRead: () => void }): React.JSX.Element {
  return (
    <header style={{ padding: '16px', borderBottom: `1px solid ${props.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <h3 style={{ fontFamily: FONTS.heading, fontSize: '13px', letterSpacing: '0.2em', textTransform: 'uppercase', color: props.colors.text, margin: 0 }}>
        Notifications
      </h3>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={props.onMarkAllRead} style={{ border: 'none', background: 'transparent', color: '#01A9ED', cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px' }}>Mark all read</button>
        <button onClick={props.onClose} style={{ border: 'none', background: 'transparent', color: props.colors.textSecondary, cursor: 'pointer', fontFamily: FONTS.body, fontSize: '14px' }}>×</button>
      </div>
    </header>
  );
}

/** Single notification row. */
function Item(props: { item: AdminNotification; colors: Colors; onClick: () => void }): React.JSX.Element {
  const unread = props.item.read_at === null;
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        width: '100%', textAlign: 'left',
        padding: '10px 12px', margin: '6px 0',
        borderRadius: '8px',
        border: `1px solid ${props.colors.border}`,
        background: unread ? props.colors.card : 'transparent',
        color: props.colors.text,
        cursor: 'pointer',
        display: 'block',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: SEVERITY_COLORS[props.item.severity] }} />
        <span style={{ fontFamily: FONTS.heading, fontSize: '12px', letterSpacing: '0.05em', color: props.colors.text }}>
          {props.item.title}
        </span>
      </div>
      {props.item.body && (
        <div style={{ fontFamily: FONTS.body, fontSize: '12px', color: props.colors.textSecondary, marginBottom: '4px' }}>
          {props.item.body}
        </div>
      )}
      <div style={{ fontFamily: FONTS.body, fontSize: '10px', color: props.colors.textSecondary }}>
        {new Date(props.item.created_at).toLocaleString()}
      </div>
    </button>
  );
}
